import {
  lookupBoardId,
  useBoardFullInfo,
  useBoardIntegrations,
  useMondayTask,
} from "frontend/hooks/use-board-integrations";
import { SyncService } from "frontend/services/syncService";
import { Group, Rect, Text } from "react-konva";
import { Html } from "react-konva-utils";
import { RW } from "shared/datamodel/replicache-wrapper/mutators";
import { IntegrationType } from "shared/integrations/integration";
import { IntegrationItem } from "shared/datamodel/schemas/integration-item";
import { useCurrentCanvasValue } from "../../canvas-context";
import MondayTaskElement from "../monday-task-element";
import { ITraits, Trait } from "../../elements-toolbar/elements-toolbar-types";
import BaseCanvasElement from "../base-canvas-element";
import { noop } from "frontend/utils/fn-utils";
import consts from "shared/consts";
import { useEffect, useRef } from "react";
import { useAtomValue } from "jotai";
import { transformerRefAtom, undoRedoStackAtom } from "state-atoms";
import { BoardIntegrationLoadingState } from "frontend/hooks/use-board-integrations.types";
import { MondayElement_integration } from "../../integrations/integrations.monday.types";
import { useMondayItem } from "../../integrations/hooks/use-monday-item";
import { MondayColumn } from "shared/integrations/mapper";
import { useIntegrationsProvider } from "frontend/hooks/use-integrations-provider";
import { getErrorConfig } from "./monday-error-messages";

export function IntegrationCanvasElementFreeStanding({
  syncService,
  id,
  element,
  isEditingLink,
  changeElement,
  isEditable = true,
  onResize,
}: {
  syncService?: SyncService<RW> | null;
  id: string;
  element: IntegrationItem;
  isEditable?: boolean;
  isEditingLink: boolean;
  changeElement: any;
  onResize: any;
}) {
  return (
    <BaseCanvasElement
      id={id}
      type={"integrationItem"}
      x={element.x}
      y={element.y}
      element={element}
      onResize={onResize}
      isSelectable={true}
      isEditingLink={isEditingLink}
      changeElement={changeElement}
    >
      {element.integrationType == IntegrationType.monday && (
        <MondayItemElement
          id={id}
          syncService={syncService}
          isEditable={isEditable}
          color={element.fill}
          integrationId={element.integrationId}
          itemId={element.configuration.itemId}
          configuration={element.configuration}
          reflectExpanded={element.expanded || false}
        />
      )}
    </BaseCanvasElement>
  );
}

export function IntegrationCanvasElementInStack({
  syncService,
  id,
  element,
  isEditingLink,
  changeElement,
  isEditable = true,
}: {
  syncService?: SyncService<RW>;
  id: string;
  element: IntegrationItem;
  isEditable?: boolean;
  isEditingLink: boolean;
  changeElement: any;
}) {
  return (
    <BaseCanvasElement
      id={id}
      type={"integrationItem"}
      x={0}
      y={0}
      element={{ ...element, scaleX: 1, scaleY: 1 }}
      onResize={noop}
      isSelectable={true}
      isEditingLink={isEditingLink}
      changeElement={changeElement}
    >
      {element.integrationType == IntegrationType.monday && (
        <MondayItemElement
          id={id}
          syncService={syncService}
          isEditable={isEditable}
          integrationId={element.integrationId}
          itemId={element.configuration.itemId}
          configuration={element.configuration}
          reflectExpanded={null}
        />
      )}
    </BaseCanvasElement>
  );
}

export function MondayItemElement({
  id,
  color,
  integrationId,
  itemId,
  configuration = { itemId },
  reflectExpanded,
  syncService,
  isEditable = true,
  // Used as a hack to re-render the element.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onChangeValue,
}: {
  id: string;
  color?: string | null;
  integrationId: string;
  itemId: string;
  configuration?: any;
  syncService?: SyncService<RW> | null;
  isEditable?: boolean;
  reflectExpanded?: boolean | null;
  onChangeValue?: () => void;
}) {
  const transformerRef = useAtomValue(transformerRefAtom);
  const [{ documentId, pusherChannel }] = useCurrentCanvasValue();

  const { integrationConfig, isLoadingAccountIntegrations, loadingBoardIntegrationsState } = useMondayTask(
    documentId,
    integrationId,
    itemId,
    pusherChannel
  );

  // We need to load the item for integrationsConfig to appear.
  // On new monday items in card stack, this does not run automatically.
  const { loadItems } = useIntegrationsProvider();
  useEffect(() => {
    loadItems({ [integrationId]: [itemId] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [integrationId, itemId]);

  const { boardIntegrations } = useBoardIntegrations(documentId);

  const boardId = lookupBoardId(integrationId, boardIntegrations);

  const boardInfo = useBoardFullInfo(documentId, integrationId, boardId);
  const allColumns: MondayColumn[] = boardInfo?.data?.board?.columns ?? [];
  const boardName = boardInfo?.data?.board?.name;

  const { data: serverItem, isLoading, error, mutate } = useMondayItem(itemId);

  const item = serverItem && allColumns?.length ? { ...serverItem, boardName, columns: allColumns } : serverItem;

  const undoRedoStack = useAtomValue(undoRedoStackAtom);

  const showLoader = isLoading || !item || !integrationConfig?.columns;
  const showFailure =
    !!error ||
    isLoadingAccountIntegrations == BoardIntegrationLoadingState.failed ||
    loadingBoardIntegrationsState == BoardIntegrationLoadingState.failed;

  useEffect(() => {
    transformerRef?.current?.forceUpdate();
  }, [transformerRef, showLoader, showFailure]);

  function onChangeConfiguration(configuration: any) {
    syncService?.mutate.updateIntegrationItemConfiguration({ id, configuration });
  }

  function onChangeExpanded(expanded: boolean) {
    undoRedoStack?.patchElement(id, (element: IntegrationItem) => {
      element.expanded = expanded;
    });
  }

  if (showFailure) {
    const errorConfig = getErrorConfig(error?.code);
    const cardWidth = consts.DEFAULTS.MONDAY_CARD_WIDTH;
    const cardHeight = consts.DEFAULTS.MONDAY_CARD_HEIGHT;
    const iconSize = 53;
    const iconY = 70;
    const textY = iconY + iconSize;

    return (
      <Group>
        <Rect
          width={cardWidth}
          height={cardHeight}
          shadowColor={"rgba(0, 0, 0, 0.12)"}
          shadowBlur={12}
          shadowOffsetY={4}
          stroke={"#EBEDF3"}
          strokeWidth={1}
          shadowForStrokeEnabled={false}
          fill={"white"}
        />
        <ErrorIconKonva
          x={cardWidth / 2 - iconSize / 2}
          y={iconY}
          width={iconSize}
          height={iconSize}
          icon={errorConfig.icon}
        />
        <Text
          y={textY}
          width={cardWidth}
          text={errorConfig.message}
          fontFamily="Poppins"
          fontSize={16}
          fill="#1E1E1E"
          align="center"
          lineHeight={1.3}
        />
      </Group>
    );
  }

  if (showLoader) {
    return (
      <Group>
        <Loader width={consts.DEFAULTS.MONDAY_CARD_WIDTH} height={consts.DEFAULTS.MONDAY_CARD_HEIGHT} />
      </Group>
    );
  }

  const orderedSelectedColumns = allColumns?.filter((column) =>
    integrationConfig.columns?.find((selectedColumn: any) => selectedColumn.id === column.id)
  );

  return (
    <MondayTaskElement
      id={id}
      color={color}
      element={new MondayElement_integration(item)}
      onChangeColumnValue={(id, value) => {
        const columnDefinition = allColumns.find((col) => col.id === id);
        if (!columnDefinition) {
          console.error("updating column that is not in board anymore?!");
          return Promise.resolve({ status: "faild" });
        }
        mutate(id, value, columnDefinition);
        return Promise.resolve({ status: "success" });
      }}
      documentId={documentId!}
      allColumns={allColumns}
      selectedColumns={orderedSelectedColumns}
      configuration={configuration}
      onChangeConfiguration={onChangeConfiguration}
      reflectExpanded={reflectExpanded ?? null}
      onChangeReflectExpanded={onChangeExpanded}
      integrationId={integrationId}
      isEditable={isEditable}
    />
  );
}

export function MondayItemFailedToLoad() {
  return (
    <Group>
      <Rect
        width={consts.DEFAULTS.MONDAY_CARD_WIDTH}
        height={consts.DEFAULTS.MONDAY_CARD_HEIGHT}
        shadowColor={"rgba(0, 0, 0, 0.12)"}
        shadowBlur={12}
        shadowOffsetY={4}
        stroke={"#EBEDF3"}
        strokeWidth={1}
        shadowForStrokeEnabled={false}
        fill={"white"}
      />
      <Text x={30} y={25} text="Failed loading this item" fontFamily="Poppins" fontSize={18} />
      <Text
        x={30}
        y={75}
        width={consts.DEFAULTS.MONDAY_CARD_WIDTH - 60}
        text={"It happens when you no longer have permissions, or the integration was removed from this canvas"}
        fontFamily="Poppins"
        fill={"gray"}
        fontSize={16}
        lineHeight={1.3}
      />
    </Group>
  );
}

export function MondayItemLoader() {
  return (
    <Group>
      <Loader width={consts.DEFAULTS.MONDAY_CARD_WIDTH} height={consts.DEFAULTS.MONDAY_CARD_HEIGHT} />
    </Group>
  );
}

export function MondayItemInLiveCardStack({
  id,
  color,
  integrationId,
  documentId,
  configuration,
  allColumns,
  selectedColumns,
  isEditable = true,
}: {
  id: string;
  color?: string | null;
  integrationId: string;
  documentId: string;
  configuration: any;
  allColumns: any[];
  selectedColumns: any[];
  isEditable?: boolean;
}) {
  const { data, isLoading, error, mutate } = useMondayItem(configuration.itemId);

  if (isLoading || !data) {
    return <MondayItemLoader />;
  }
  if (error) {
    return <MondayItemFailedToLoad />;
  }
  return (
    <MondayTaskElement
      id={id}
      color={color}
      element={new MondayElement_integration(data)}
      onChangeColumnValue={(id, value) => {
        const columnDefinition = allColumns.find((col) => col.id === id);
        if (!columnDefinition) {
          console.error("updating column that is not in board anymore?!");
          return Promise.resolve({ status: "faild" });
        }
        return mutate(id, value, columnDefinition)
          .then(() => ({ status: "success" } as const))
          .catch(() => ({ status: "faild" } as const));
      }}
      documentId={documentId!}
      allColumns={allColumns}
      selectedColumns={selectedColumns}
      configuration={configuration}
      onChangeConfiguration={noop}
      integrationId={integrationId}
      isEditable={isEditable}
      reflectExpanded={null}
      onChangeReflectExpanded={noop}
    />
  );
}

export function integrationItemTraits(element: IntegrationItem): ITraits {
  const { fill, integrationId, configuration } = element;
  return {
    integrationItemColor: {
      fill,
      integrationId,
      // Configuration shouldn't be undefined, but Maria managed to break it.
      // Therefore, this question mark is dedicated to Maria.
      colorBy: configuration?.colorBy,
    },
  };
}

export function integrationItemValidateTrait(element: IntegrationItem, trait: Trait, value: any) {
  if (trait == Trait.integrationItemColor) {
    const configuration = {
      ...element.configuration,
      colorBy: value.colorBy,
    };

    return { fill: value.fill, configuration };
  }

  return value;
}

interface LoaderProps {
  width: number;
  height: number;
}

function Loader({ width, height }: LoaderProps) {
  const rectRef = useRef<any>(null);
  const animationRef = useRef<number>();

  useEffect(() => {
    let startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = (elapsed % 2000) / 2000; // 2 second cycle

      if (rectRef.current) {
        // Create shimmer effect by modulating the fill color
        const baseGray = 245; // Light gray base
        const shimmerIntensity = 15; // How much lighter the shimmer gets
        const shimmerValue = Math.sin(progress * Math.PI * 2) * shimmerIntensity;
        const grayValue = Math.max(0, Math.min(255, baseGray + shimmerValue));

        rectRef.current.fill(`rgb(${grayValue}, ${grayValue}, ${grayValue})`);
        rectRef.current.getLayer()?.batchDraw();
      }

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return (
    <Rect
      ref={rectRef}
      width={width}
      height={height}
      shadowColor={"rgba(0, 0, 0, 0.12)"}
      shadowBlur={12}
      shadowOffsetY={4}
      stroke={"#EBEDF3"}
      strokeWidth={1}
      shadowForStrokeEnabled={false}
      fill={"#f5f5f5"}
    />
  );
}

interface ErrorIconKonvaProps {
  x: number;
  y: number;
  width: number;
  height: number;
  icon: string;
}

function ErrorIconKonva({ x, y, width, height, icon }: ErrorIconKonvaProps) {
  return (
    <Group x={x} y={y}>
      <Rect width={width} height={height} fill="transparent" />
      <Group x={width / 2} y={height / 2}>
        <Html groupProps={{ x: -width / 2, y: -height / 2 }}>
          <div
            dangerouslySetInnerHTML={{ __html: icon }}
            style={{
              width,
              height,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          />
        </Html>
      </Group>
    </Group>
  );
}
